import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Grid, InputLabel, OutlinedInput, Switch, FormControl, Select, MenuItem, Typography } from '@mui/material';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ApiHelper from 'utils/apiHelper';
import { validateNumber } from 'utils/commonHelpers';
import {
    gridSpacing,
    dashboardSelectionOrderValues,
    dashboardSelectionOptions,
    equivalentOptions,
    avoidanceOptions,
    sequesteredOptions
} from 'utils/constants';

const GeneralSettings = () => {
    const [settingsData, setSettingsData] = useState('');

    const getSettingsData = () => {
        ApiHelper.get('/api/settings/general_settings/')
            .then((res) => {
                setSettingsData(res.data);
            })
            .catch(() => toast.error('Failed to get the settings data'));
    };

    useEffect(() => {
        getSettingsData();
    }, []);

    const handleChangeSettingsData = (e) => {
        if (e.target.value === '') toast.error('Please type the value');
        const type = e.target.id === 'generalSettings' ? 'integer' : 'number';
        if (validateNumber(e.target.value, type) === true) {
            setSettingsData({
                ...settingsData,
                [e.target.id]: {
                    ...settingsData[e.target.id],
                    [e.target.name]: e.target.value
                }
            });
        } else {
            if (type === 'integer') toast.error('It should be an integer value.');
            else toast.error('It should be a number');
        }
    };

    const handleChangeGreenhouseData = (e) => {
        setSettingsData({
            ...settingsData,
            generalSettings: {
                ...settingsData['generalSettings'],
                [e.target.name]: e.target.value
            }
        });
    };

    const handleChangeDashboardPanelOrder = (e) => {
        let newData = { ...settingsData };
        for (const key in newData.selectedDashboards) {
            if (newData.selectedDashboards.hasOwnProperty(key)) {
                if (Number(e.target.value) !== 0 && newData.selectedDashboards[key].orderVal === Number(e.target.value)) {
                    newData.selectedDashboards[key].orderVal = 0;
                }
            }
        }
        newData.selectedDashboards[e.target.name]['orderVal'] = Number(e.target.value);
        setSettingsData(newData);
    };

    const handleSubmit = () => {
        if (
            settingsData.generalSettings.showRows === '' ||
            settingsData.generalSettings.checkinInterval === '' ||
            settingsData.generalSettings.showUnlicensedFor === '' ||
            settingsData.generalSettings.unlicensedAfter === '' ||
            settingsData.generalSettings.dataRetention === '' ||
            settingsData.energyValues.energyCost === '' ||
            settingsData.energyValues.co2Cost === '' ||
            settingsData.energyValues.greenPowerPercent === ''
        ) {
            toast.error('There are some empty values');
            return true;
        }
        ApiHelper.put(`/api/settings/general_settings/${settingsData.generalSettings.id}/`, {
            show_rows: settingsData.generalSettings.showRows,
            checkin: settingsData.generalSettings.checkinInterval,
            show_unlicensed_for: settingsData.generalSettings.showUnlicensedFor,
            unlicensed_after: settingsData.generalSettings.unlicensedAfter,
            show_unlicensed_devices: settingsData.generalSettings.showUnlicensedDevices,
            keep_days: settingsData.generalSettings.dataRetention,
            equivalent_id: settingsData.generalSettings.equivalentId,
            avoidance_id: settingsData.generalSettings.avoidanceId,
            sequestered_id: settingsData.generalSettings.sequesteredId,
            energy_cost: settingsData.energyValues.energyCost,
            co2cost: settingsData.energyValues.co2Cost,
            green_power_percent: settingsData.energyValues.greenPowerPercent,
            selected_dashboards: settingsData.selectedDashboards,
            debuglevel_id: settingsData.levelSettings.debugLevelId
        })
            .then(() => {
                getSettingsData();
                toast.success('Successfully Updated.');
            })
            .catch(() => {
                toast.error('Failed to update the settings data.');
            });
    };

    if (!settingsData) {
        return null;
    } else {
        return (
            <MainCard title="General Settings" cardHeaderTitle="System Options" cardHeaderShow={true}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} sx={{ position: 'relative', mt: 2, mb: 2 }}>
                        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
                            <Button size="large" color="secondary" variant="contained" onClick={handleSubmit}>
                                Save
                            </Button>
                            <Button onClick={getSettingsData}>Cancel</Button>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="General Settings">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Devices to show (Rows) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.generalSettings.showRows}
                                        name="showRows"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Data retention (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.generalSettings.dataRetention}
                                        name="dataRetention"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Check-in interval (Minutes) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.generalSettings.checkinInterval}
                                        name="checkinInterval"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Unlicense After (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.generalSettings.unlicensedAfter}
                                        name="unlicensedAfter"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Show Unlicensed For (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.generalSettings.showUnlicensedFor}
                                        name="showUnlicensedFor"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Show Unlicensed Devices </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={settingsData.generalSettings.showUnlicensedDevices}
                                        name="showUnlicensedDevices"
                                        id="generalSettings"
                                        onChange={(e) =>
                                            setSettingsData({
                                                ...settingsData,
                                                [e.target.id]: {
                                                    ...settingsData[e.target.id],
                                                    [e.target.name]: e.target.checked
                                                }
                                            })
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Energy Values">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Energy Cost ($/kwh) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.energyValues.energyCost}
                                        name="energyCost"
                                        id="energyValues"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>CO2 (Lbs/kwh) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.energyValues.co2Cost}
                                        name="co2Cost"
                                        id="energyValues"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Green Power (%) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settingsData.energyValues.greenPowerPercent}
                                        name="greenPowerPercent"
                                        id="energyValues"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Greenhouse Gas Comparisons">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Preferred Equivalent</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settingsData.generalSettings.equivalentId}
                                            name="equivalentId"
                                            onChange={handleChangeGreenhouseData}
                                        >
                                            {equivalentOptions.map((equivalent, key) => (
                                                <MenuItem value={equivalent.value} key={key}>
                                                    {equivalent.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Preferred Avoidance</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settingsData.generalSettings.avoidanceId}
                                            name="avoidanceId"
                                            onChange={handleChangeGreenhouseData}
                                        >
                                            {avoidanceOptions.map((avoidance, key) => (
                                                <MenuItem value={avoidance.value} key={key}>
                                                    {avoidance.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Preferred Sequestration</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settingsData.generalSettings.sequesteredId}
                                            name="sequesteredId"
                                            onChange={handleChangeGreenhouseData}
                                        >
                                            {sequesteredOptions.map((sequestered, key) => (
                                                <MenuItem value={sequestered.value} key={key}>
                                                    {sequestered.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SubCard>
                        <SubCard title="Logging Level" sx={{ mt: 4 }}>
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Level</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            id="levelSettings"
                                            value={settingsData.levelSettings.debugLevelId}
                                            name="debugLevelId"
                                            onChange={(e) => {
                                                setSettingsData({
                                                    ...settingsData,
                                                    levelSettings: {
                                                        ...settingsData['levelSettings'],
                                                        [e.target.name]: Number(e.target.value)
                                                    }
                                                });
                                            }}
                                        >
                                            {settingsData.levelOptions.map((level, key) => (
                                                <MenuItem value={level.levelid} key={key}>
                                                    {level.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Dashboard Panel Selections">
                            <Grid container>
                                <Typography>Please select the dashboard order to show the plots at Home page</Typography>
                                {dashboardSelectionOptions.map((data, key) => (
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }} key={key}>
                                        <InputLabel>{data.label}</InputLabel>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Select
                                                displayEmpty
                                                inputProps={{ 'aria-label': 'Without label' }}
                                                name={data.value}
                                                value={
                                                    settingsData.selectedDashboards[data.value]
                                                        ? settingsData.selectedDashboards[data.value]['orderVal']
                                                        : 0
                                                }
                                                onChange={handleChangeDashboardPanelOrder}
                                            >
                                                <MenuItem value={0}>Don't show</MenuItem>
                                                {dashboardSelectionOrderValues.map((value, key) => (
                                                    <MenuItem value={value} key={key}>
                                                        {value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default GeneralSettings;
